/**
 * PaymentsReimportSection — R5-RI
 *
 * Extrahiert aus PaymentsSidebar (Audit 2026-09-01, Design-Paritaet Payments):
 * die Vorlage (payments.html) zeigt "Re-import from chain" als Button neben
 * "+ Manual payment" im Seitenkopf, nicht in der Sidebar — PaymentsPage
 * rendert diese Sektion daher jetzt im Header. Verhalten unveraendert:
 * Confirm-Dialog -> reimportPayments() -> Ergebnis-/Fehlermeldung.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePaymentsReimport } from './usePaymentsReimport'

/** Anzeigedauer des Ergebnis-Hinweises (R5-Review F9). */
const RESULT_RESET_MS = 8000

type ReimportConfirmProps = {
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

function PaymentsReimportConfirmDialog({ onConfirm, onCancel, isLoading }: ReimportConfirmProps) {
  const { t } = useTranslation('payments')

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('reimport.dialogLabel')}
      data-testid="payments-reimport-confirm-dialog"
    >
      <div className="card modal-card">
        <p>{t('reimport.confirmMessage')}</p>
        <div className="wallet-connect-actions">
          <button
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="btn-confirm-payments-reimport"
            aria-busy={isLoading}
          >
            {isLoading ? t('reimport.reimporting') : t('reimport.confirmButton')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="btn-cancel-payments-reimport"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

export function PaymentsReimportSection() {
  const { t } = useTranslation('payments')
  const { reimportPayments, isPending } = usePaymentsReimport()
  const [showConfirm, setShowConfirm] = useState(false)
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // R5-Review F9: der Auto-Ausblend-Timer haengt an einer Ref und wird beim
  // Unmount geraeumt — sonst feuert setResult(null) auf einer toten Komponente,
  // wenn der Nutzer die Seite innerhalb der 8s verlaesst.
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [])

  // i18n100-1 (Cluster-C-Muster): der Hook wirft stabile Codes, hier wird uebersetzt (t()).
  function errorMessage(outcome: { error: 'RATE_LIMITED' | 'REIMPORT_FAILED'; retryAfter?: number }): string {
    if (outcome.error === 'RATE_LIMITED') {
      if (outcome.retryAfter) {
        const minutes = Math.max(1, Math.ceil(outcome.retryAfter / 60))
        return t('reimport.rateLimitedWithTime', { minutes })
      }
      return t('reimport.rateLimited')
    }
    return t('reimport.error')
  }

  async function handleConfirm() {
    setShowConfirm(false)
    const outcome = await reimportPayments()
    if (outcome.ok) {
      setResult({ type: 'success', message: t('reimport.successCount', { count: outcome.walletCount }) })
    } else {
      setResult({ type: 'error', message: errorMessage(outcome) })
    }
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    resetTimerRef.current = setTimeout(() => {
      resetTimerRef.current = null
      setResult(null)
    }, RESULT_RESET_MS)
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => setShowConfirm(true)}
        disabled={isPending}
        aria-label={t('reimport.buttonLabel')}
        title={t('reimport.buttonLabel')}
      >
        {isPending ? t('reimport.reimporting') : t('reimport.button')}
      </button>
      {result && (
        <p
          role="status"
          aria-live="polite"
          className={result.type === 'success' ? 'text-success' : 'text-danger'}
          style={{ fontSize: 12, marginTop: 8, textAlign: 'right' }}
        >
          {result.message}
        </p>
      )}
      {showConfirm && (
        <PaymentsReimportConfirmDialog
          isLoading={isPending}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  )
}
